'use client';

import {
  ProductCardImage,
  type ProductCardImageProps,
} from '@curology/ui-components-web/react/Card/ProductCard/ProductCardImage';
import { Label } from '@curology/ui-components-web/react/Label';
import React from 'react';
import Slider from 'react-slick';

import { getSwipeDirection } from '@/helpers/productSelectionAndCheckout/helpers';
import {
  type Card,
  type Patient,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import '../css/productCardImageCarouselStyles.css';

// Logic comes from:
// resources/assets/jsx/signup/views/recommendations/components/newCurologyControlExperience/components/productCard/productImagesCarousel.tsx

type ProductCardImageCarouselProps = {
  aspectRatio: ProductCardImageProps['aspectRatio'];
  card: Card;
  patient: Patient;
  showRecommendationTag?: boolean;
};

const ProductCardImageCarousel = ({
  aspectRatio,
  card,
  patient,
  showRecommendationTag = true,
}: ProductCardImageCarouselProps) => {
  // Uses the string aspectRatio prop (e.g., '3:2') to calculate the percentage height that must be set for paddingTop below to ensure that the correct aspect ratio is used for the carousel images.
  let heightPercentage;
  if (aspectRatio?.includes(':')) {
    const [widthRatio, heightRatio] = aspectRatio.split(':');
    heightPercentage = (Number(heightRatio) / Number(widthRatio)) * 100;
  } else if (aspectRatio === 'square') {
    heightPercentage = 100;
  }

  return (
    <div
      className="product-card-image-carousel-container"
      style={heightPercentage ? { paddingTop: `${heightPercentage}%` } : {}}
    >
      <div className="product-card-image-carousel-inner-container">
        <Slider
          accessibility={true}
          arrows={false}
          beforeChange={(current: number, next: number) => {
            trackEvent(AnalyticsEvent.PRODUCT_CARD_CAROUSEL_SWIPED, {
              imageIndex: next,
              offerId: patient?.introOffer?.id,
              productName: card.name,
              swipeDirection: getSwipeDirection(
                current,
                next,
                card.images.length - 1,
              ),
            });
          }}
          dots={true}
          draggable={true}
          infinite={true}
        >
          {card.images.map((image) => {
            return (
              <ProductCardImage
                alt={image.alt}
                aspectRatio={aspectRatio}
                className="cur-cursor-default"
                key={image.name}
                onClick={(event) => event.stopPropagation()}
                src={image.url}
              />
            );
          })}
        </Slider>
        {showRecommendationTag && card.recommendationText && (
          <div className="cur-absolute cur-flex cur-flex-wrap cur-gap-1 cur-left-0 cur-right-0 cur-top-0">
            <Label>{card.recommendationText.label}</Label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCardImageCarousel;
